import { apiFetch } from '../../services/api/client';

const TOKEN_REFRESH_BUFFER_MS = 60 * 1000;

let cachedToken: string | null = null;
let cachedExpiresAt = 0;
let cachedArtistId: string | null = null;
let inFlight: Promise<string> | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'currentArtistId') {
      clearDistroToken();
    }
  });
}

function getCurrentArtistId(): string | null {
  return localStorage.getItem('currentArtistId');
}

function isTokenValid(artistId: string) {
  return (
    cachedToken &&
    cachedArtistId === artistId &&
    cachedExpiresAt - Date.now() > TOKEN_REFRESH_BUFFER_MS
  );
}

export function clearDistroToken(): void {
  cachedToken = null;
  cachedExpiresAt = 0;
  cachedArtistId = null;
  inFlight = null;
}

/**
 * Fetch a Nova-issued JWT for the external Distro app (POST /distro/token).
 * Requires a selected artist (x-artist-id); available to all users with artist access.
 */
export async function getDistroToken(forceRefresh = false): Promise<string> {
  const artistId = getCurrentArtistId();
  if (!artistId) {
    const error = new Error('Artist not selected');
    (error as { code?: string }).code = 'MISSING_ARTIST_ID';
    throw error;
  }

  if (cachedArtistId && cachedArtistId !== artistId) {
    clearDistroToken();
  }

  if (!forceRefresh && isTokenValid(artistId)) {
    return cachedToken as string;
  }

  if (inFlight && !forceRefresh) {
    return inFlight;
  }

  inFlight = (async () => {
    const data = await apiFetch('/distro/token', { method: 'POST' });
    if (!data?.token) {
      throw new Error('Invalid Distro token response');
    }

    const expiresAtMs = data.expiresAt ? Date.parse(data.expiresAt) : NaN;
    cachedToken = data.token;
    cachedExpiresAt = Number.isFinite(expiresAtMs)
      ? expiresAtMs
      : Date.now() + 15 * 60 * 1000;
    cachedArtistId = artistId;

    return data.token as string;
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}
